import { useMutation } from '@apollo/react-hooks';
import { Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAuth } from 'src/hooks/use-auth';
import { useCurrentViewPropertySession } from 'src/hooks/use-current-view-property-session';
import { useOperations } from 'src/hooks/use-operations';
import { useViewer } from 'src/hooks/use-viewer';
import ButtonBase from '~components/button-base';
import Popover from '~components/popover';
import Tooltip from '~components/tooltip';
import Typography from '~components/typography';
import { USER_FORGOT_PASSWORD } from '~containers/auth/gql';
import { useSideNavigationOpenContext } from '~containers/auth-page-layout/side-navigation-context';
import CreateDirectLetBookingModal from '~containers/creating-booking/create-booking-modal';
import GlobalSearchBar from '~containers/global-search-bar';
import { useRegisterReleaseHintReference } from '~containers/release-hint/use-register-release-hint-reference';
import { ControlIconAdd, NavMenuCollaspsed, NavMenuExpand } from '~svg-components';
import { BasicTask } from '~svg-components';
import getShortName from '~utils/get-short-name';
import hexToRGBA from '~utils/hex-to-rgba';
import { useBoolean } from '~utils/use-boolean';
import useDisplayResponseErrorToast from '~utils/use-display-response-error-toast';
import useDisplayToast from '~utils/use-display-toast';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '56px',
    width: '100%',
    padding: '0 32px 0 24px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  stage: {
    backgroundImage: `linear-gradient(to bottom, ${hexToRGBA(
      theme.colors.orange,
      0.3
    )}, ${hexToRGBA(theme.colors.orange, 0)})`,
    position: 'relative',
    '&::before': {
      content: "''",
      width: '100%',
      height: 4,
      backgroundColor: theme.colors.orange,
      position: 'absolute',
      top: 0,
      left: 0,
    },
  },

  navSwitchBtn: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    borderRadius: theme.borderRadius.small,
    cursor: 'pointer',
    zIndex: '999',

    '&:hover': {
      backgroundColor: theme.colors.greyE8,
    },
  },

  rightAction: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },

  info: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    cursor: 'pointer',
    marginRight: 12,
  },

  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    fontSize: '12px',
    fontWeight: 'bold',
    borderRadius: '50%',
    color: theme.colors.grey99,
    backgroundColor: theme.colors.greyE8,
  },

  name: {
    fontSize: '14px',
    lineHeight: '24px',
    marginLeft: '8px',
    color: theme.colors.dark,
  },

  separator: {
    width: '1px',
    height: '18px',
    marginLeft: 12,
    backgroundColor: theme.colors.greyCc,
    cursor: 'default',
  },

  role: {
    fontSize: '14px',
    lineHeight: '24px',
    color: theme.colors.grey66,
  },

  popover: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    marginTop: 12,
  },

  popInfo: {
    margin: '20px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  popAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    fontSize: '14px',
    fontWeight: 'bold',
    borderRadius: '50%',
    color: theme.colors.grey99,
    backgroundColor: theme.colors.greyE8,
  },

  popContact: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: '12px',
  },

  popName: {
    fontSize: '16px',
    lineHeight: '28px',
    color: theme.colors.dark,
  },

  popEmail: {
    fontSize: '12px',
    lineHeight: '20px',
    color: theme.colors.grey99,
  },

  popDivider: {
    height: '1px',
    margin: '0 12px',
  },

  popAction: {
    margin: '8px 0',
  },

  popBtn: {
    justifyContent: 'flex-start',
    padding: '0 20px',
    height: '32px',
    width: '100%',
    fontSize: '14px',
    color: theme.colors.dark,
    '&:hover': {
      backgroundColor: theme.colors.greyFa,
    },
  },
  disabled: {
    color: theme.colors.greyCc,
  },

  newBooking: {
    marginLeft: '16px',
    padding: '7px 12px',
    border: `1px solid ${theme.colors.greyCc}`,
    borderRadius: theme.borderRadius.small,
    backgroundColor: hexToRGBA(theme.colors.white, 0.8),
    '& svg': {
      width: '12px',
      height: '12px',
      marginRight: '9px',
      fill: theme.colors.grey66,
    },
    '&:hover': {
      backgroundColor: theme.colors.greyE8,
    },
  },

  approvalCenterRoot: {
    ...theme.flex.center,
    padding: '8px 19px',
    margin: '4px 0',
    '&:hover': {
      backgroundColor: theme.colors.greyFa,
    },
  },

  approvalCenterBtn: {
    ...theme.typography.dropdown,
    margin: '0 8px',
    justifyContent: 'flex-start',
    flex: 1,
  },

  approvalCenterMsgRoot: {
    padding: '2px 8px',
    backgroundColor: theme.colors.orange,
    borderRadius: '10px',
  },

  approvalCenterOrangeDot: {
    position: 'absolute',
    top: 0,
    right: '-2px',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.colors.orange,
  },

  approvalCenterOrangeTips: {
    padding: '4px 12px',
    borderRadius: '4px',
    position: 'relative',
    marginRight: 12,
    backgroundColor: theme.colors.orangeBg,

    '&::before': {
      content: "''",
      width: 0,
      height: 0,
      position: 'absolute',
      top: '6px',
      right: '-6px',
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: `8px solid ${theme.colors.orangeBg}`,
    },
  },

  avatarRoot: {
    position: 'relative',
  },
}));

function TopBar({ isSticky }) {
  const { t } = useTranslation(['booking']);
  const displayToast = useDisplayToast();
  const displayResponseErrorToast = useDisplayResponseErrorToast();
  const classes = useStyles();
  const { canRead: canReadGlobalSearchBar } = useOperations('dashboard');
  const { canCreate: canCreateNewBooking } = useOperations('booking');
  const { canRead: canReadApprovalCenter } = useOperations('approval-center');
  const { name, email, currentRole, processingTicketsCount, topLandlord } = useViewer();
  const shortName = useMemo(() => getShortName(name), [name]);

  const { isSideNavigationOpen, toggleSideNavigation } = useSideNavigationOpenContext();

  const [
    isCreateBookingModalOpen,
    { setTrue: openCreateBookingModal, setFalse: closeCreateBookingModal },
  ] = useBoolean();

  const [
    {
      currentViewProperty: { id: propertyId },
    },
  ] = useCurrentViewPropertySession();

  const [anchorEl, setAnchorEl] = useState(null);
  const [hideNewBookingBtn, setHideNewBookingBtn] = useState(false);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const [forgotPassword, { loading }] = useMutation(USER_FORGOT_PASSWORD, {
    onError: displayResponseErrorToast,
    onCompleted: () => {
      handleClose();
      displayToast('success', t('header.forgot_password.email.success_tips'));
    },
  });
  const open = Boolean(anchorEl);

  const history = useHistory();
  const [, { clearToken }] = useAuth();

  const logout = () => {
    window.FreshworksWidget('hide');
    history.replace('/auth/login');
    clearToken();
    sessionStorage.removeItem('BOOKINGFILTER');
  };

  const resetPassword = () => {
    if (loading) return;
    forgotPassword({ variables: { email, topLandlordId: topLandlord?.id } });
  };

  const focusHideNewBookingBtn = (type) => {
    if (type === 'focus') {
      setHideNewBookingBtn(true);
    } else {
      setHideNewBookingBtn(false);
    }
  };

  const registerReleaseHintReference = useRegisterReleaseHintReference();

  const renderNewBookingButton = () => (
    <>
      <Tooltip title={t('booking:create_direct_let_booking')} placement="bottom">
        <ButtonBase
          ref={registerReleaseHintReference('2021-02-20', 'new-booking')}
          disableRipple
          className={classes.newBooking}
          onClick={openCreateBookingModal}
        >
          <ControlIconAdd className={classes.actionIcon} />
          <Typography variant="h6" themeColor="grey66" fontWeight="bold">
            {t('new_booking')}
          </Typography>
        </ButtonBase>
      </Tooltip>
      <CreateDirectLetBookingModal
        open={isCreateBookingModalOpen}
        onClose={closeCreateBookingModal}
        propertyId={propertyId}
        onBookingCreated={(booking) => {
          window.open(
            queryString.stringifyUrl({
              url: '/booking/booking-details',
              query: { propertyId: booking.property.id, bookingId: booking.id },
            })
          );
          closeCreateBookingModal();
        }}
      />
    </>
  );

  const renderApprovalCenterSection = () =>
    canReadApprovalCenter && (
      <div className={classes.approvalCenterRoot}>
        <BasicTask color="grey66" />
        <ButtonBase
          onClick={() => {
            window.open('/approval-center', '_self');
          }}
          className={classes.approvalCenterBtn}
        >
          {t('approval_center')}
        </ButtonBase>
        {processingTicketsCount > 0 && (
          <div className={classes.approvalCenterMsgRoot}>
            <Typography variant="h6" themeColor="white">
              {processingTicketsCount}
            </Typography>
          </div>
        )}
      </div>
    );

  return (
    <div
      className={classnames(classes.root, {
        [classes.stage]: process.env.REACT_APP_CURRENT_RUNNING_ENVIRONMENT === 'stage',
      })}
    >
      <ButtonBase className={classes.navSwitchBtn} onClick={toggleSideNavigation}>
        {isSideNavigationOpen ? (
          <NavMenuCollaspsed color="grey66" />
        ) : (
          <NavMenuExpand color="grey66" />
        )}
      </ButtonBase>

      {canReadGlobalSearchBar && (
        <GlobalSearchBar focusGlobalSearch={focusHideNewBookingBtn} isSticky={isSticky} />
      )}
      {canCreateNewBooking && !hideNewBookingBtn && renderNewBookingButton()}

      <div className={classes.rightAction}>
        {processingTicketsCount > 0 && (
          <div className={classes.approvalCenterOrangeTips}>
            <Typography variant="body2" themeColor="orange">
              {t('you_have_new_approvals_with_count', { count: processingTicketsCount })}
            </Typography>
          </div>
        )}
        <ButtonBase disableRipple className={classes.info} onClick={handleClick}>
          <div className={classes.avatarRoot}>
            <span className={classes.avatar}>{shortName}</span>
            {processingTicketsCount > 0 && <div className={classes.approvalCenterOrangeDot} />}
          </div>
          <span className={classes.name}>{t('user.welcome', { name })}</span>
          <span className={classes.separator} />
        </ButtonBase>
        <span className={classes.role}>{currentRole.role.name}</span>
      </div>

      <Popover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <div className={classes.popover}>
          <div className={classes.popInfo}>
            <span className={classes.popAvatar}>{shortName}</span>
            <div className={classes.popContact}>
              <span className={classes.popName}>{name}</span>
              <span className={classes.popEmail}>{email}</span>
            </div>
          </div>
          <Divider flexItem className={classes.popDivider} />
          {renderApprovalCenterSection()}
          <Divider flexItem className={classes.popDivider} />
          <div className={classes.popAction}>
            <ButtonBase
              disabled={loading}
              onClick={resetPassword}
              className={classnames(classes.popBtn, {
                [classes.disabled]: loading,
              })}
            >
              {t('button.reset_password')}
            </ButtonBase>
            <ButtonBase onClick={logout} className={classes.popBtn}>
              {t('button.logout')}
            </ButtonBase>
          </div>
        </div>
      </Popover>
    </div>
  );
}

TopBar.propTypes = {
  isSticky: PropTypes.bool.isRequired,
};

export default TopBar;
